import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Shield, ArrowUpRight } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0F2137] text-slate-300">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/logo-cambc.png" 
                alt="Cabinet CAMBC Conseils" 
                className="h-12 w-auto brightness-0 invert"
              />
              <div>
                <span className="font-bold text-xl text-white tracking-tight">CAMBC</span>
                <span className="block text-xs tracking-[0.15em] uppercase text-[#C5A059]">Conseils</span>
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-6 leading-relaxed">
              Votre courtier en assurance de confiance à Moissac. Conseil personnalisé pour 
              particuliers et professionnels depuis 2024.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg">
                <Shield size={14} className="text-[#C5A059]" />
                <span className="text-sm font-medium text-slate-300">ORIAS n° 24000879</span>
              </div>
              <p className="text-xs text-slate-500 px-3">SIREN : 882 747 652</p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Navigation</h4>
            <ul className="space-y-3">
              {[
                { name: 'Accueil', path: '/' },
                { name: 'Nos Assurances', path: '/services' },
                { name: 'Demander un Devis', path: '/quote' },
                { name: 'Assurance 24/7', path: '/assurance-temporaire' },
                { name: 'Carte Grise', path: '/carte-grise' },
                { name: 'Blog', path: '/blog' },
                { name: 'Contact', path: '/contact' }
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-sm text-slate-400 hover:text-[#C5A059] transition-colors flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Nos Services</h4>
            <ul className="space-y-3">
              {[
                { name: 'Assurance Auto', path: '/quote?type=auto' },
                { name: 'Assurance Habitation', path: '/quote?type=habitation' },
                { name: 'Santé & Prévoyance', path: '/quote?type=sante' },
                { name: 'Assurance Professionnels', path: '/quote?type=pro' },
                { name: 'Garantie Décennale', path: '/quote?type=decennale' },
                { name: 'Assurance Temporaire', path: '/assurance-temporaire' },
                { name: 'Démarches Carte Grise', path: '/carte-grise' }
              ].map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-sm text-slate-400 hover:text-[#C5A059] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin size={14} className="text-[#C5A059]" />
                </div>
                <div>
                  <span className="text-sm text-slate-300 block">46 bd Pierre Delbrel</span>
                  <span className="text-sm text-slate-400">82200 Moissac</span>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Phone size={14} className="text-[#C5A059]" />
                </div>
                <a href="tel:0582819120" className="text-sm text-slate-300 hover:text-[#C5A059] transition-colors font-medium">
                  05 82 81 91 20
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Mail size={14} className="text-[#C5A059]" />
                </div>
                <a href="mailto:cabinet@cambc-conseils.fr" className="text-sm text-slate-300 hover:text-[#C5A059] transition-colors">
                  cabinet@cambc-conseils.fr
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock size={14} className="text-[#C5A059]" />
                </div>
                <div className="text-sm">
                  <div className="text-slate-300">Lun-Ven : 9h-12h / 14h-18h</div>
                  <div className="text-[#C5A059] text-xs mt-1 font-medium">Souscription 24h/24 en ligne</div>
                </div>
              </li>
            </ul>

            {/* Espace Apporteur Link */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <Link 
                to="/login" 
                className="flex items-center gap-2 text-sm text-[#C5A059] hover:text-white transition-colors font-medium"
              >
                <Shield size={14} />
                Espace Apporteur
                <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
            <p>© {currentYear} Cabinet CAMBC Conseils — Alexandre MEUX. Tous droits réservés.</p>
            <div className="flex items-center gap-4">
              <span>Contrôlé par l'ACPR</span>
              <span className="text-slate-600">•</span>
              <Link to="/mentions-legales" className="text-slate-400 hover:text-[#C5A059] transition-colors">
                Mentions légales
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
